import { Modal as BaseModal } from "@mui/base/Modal";
import CloseIcon from "@mui/icons-material/Close";
import {
  alpha,
  Alert,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteValue,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { css, styled } from "@mui/system";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import contactAPI, { Contact, ContactQueryResponse } from "apis/ContactAPI";
import companyAPI from "apis/CompanyAPI";
import schedulePlanAPI from "apis/SchedulePlanAPI";
import scheduleAPI, {
  Schedule,
  ScheduleResponse,
} from "apis/ScheduleAPI";
import sportCourtAPI, {
  ScheduleFreeTime,
  SportCourt,
} from "apis/SportCourtAPI";
import AutocompleteSelect, {
  AutocompleteOption,
} from "components/AutocompleteSelect";
import LightSelect from "components/LightSelect";
import LightTextArea from "components/LightTextArea";
import ptBR from "date-fns/locale/pt-BR";
import { Formik } from "formik";
import useAuth from "hooks/useAuth";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SportCourtReduxWorker from "store/workers/SportCourtReduxWorker";
import { extractApiErrorMessage } from "utils/axiosErrorHandler";
import { buildSchedulePlanDetailPath } from "utils/constants";
import { startSchedulePlanProcessingMonitor } from "../../../firebase/schedulePlanProcessing";
import { SCHEDULE_STATUS } from "../event-utils";
import AppointmentDetails from "./AppointmentDetails";
import MultiScheduleSelect, { Availability } from "./MultiScheduleSelect";
import ScheduleModalMenu from "./ScheduleModalMenu";
import {
  buildScheduleCreatePayload,
  buildSchedulePlanPayload,
  DAY_OF_WEEK_OPTIONS,
  getDayOfWeekFromDate,
  normalizeAvailabilitySelection,
  RECURRENCE_FREQUENCY_OPTIONS,
  scheduleModalValidationSchema,
  ScheduleModalFormValues,
  shouldRequireDaysOfWeek,
} from "./scheduleModalFormUtils";

interface ScheduleModalProps {
  open: boolean;
  handleClose: (needsUpdate: boolean) => void;
  dataSelected: string | Date;
  selectedEvent?: string | null;
}

interface RentalData {
  sportCourtId: string;
  sportCourtValue: number;
  sportCourtRentalTime: number;
  rentalQuantity: number;
}

interface Appointment {
  id: string;
  schedulePlanId?: string | null;
  contactId: string;
  contactName: string;
  observation: string;
  whenDate: number | Date;
  timeFromTo: string;
  responsibleId: string;
  rentalData: RentalData;
  type: "RENT" | "OTHER_TYPE";
  status: string;
}

const queryInitialState = {
  filter: {
    limit: 30,
    query: {
      text: "",
      order: "name",
    },
    page: 0,
  },
  result: {
    totalPages: 0,
    totalElements: 0,
    entities: [],
  },
};

const getAvailabilityLabel = (timeFromTo: string, value: number) => {
  const exploded = timeFromTo.split(" - ");
  return `Início às ${exploded[0]} até ${exploded[1]} - R$ ${value || 0}`;
};

const isSuccessStatus = (status: number): boolean =>
  status >= 200 && status < 300;

const ScheduleModal: React.FC<ScheduleModalProps> = ({
  open,
  handleClose,
  dataSelected,
  selectedEvent,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const getInitialValues = useCallback((): ScheduleModalFormValues => {
    const parsedDate =
      dataSelected instanceof Date ? dataSelected : new Date(dataSelected);
    const recurrenceDaysOfWeek = getDayOfWeekFromDate(parsedDate);

    return {
      selectedContact: null,
      sportCourtId: "",
      date: parsedDate,
      availability: [],
      observation: "",
      status: SCHEDULE_STATUS[0].value,
        isRecurring: false,
        startDate: parsedDate,
        endDate: parsedDate,
        recurrenceFrequency: "WEEKLY",
        recurrenceDaysOfWeek,
      };
  }, [dataSelected]);

  const [scheduleForm, setScheduleForm] = useState<ScheduleModalFormValues>(
    getInitialValues()
  );
  const [sportCourts, setSportCourts] = useState<SportCourt[]>([]);
  const [listaContatos, setListaContatos] = useState<AutocompleteOption[]>([]);
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<
    Availability[]
  >([]);
  const [availabilityBlocked, setAvailabilityBlocked] = useState(false);
  const [selectedContact, setSelectedContact] =
    useState<AutocompleteOption | null>(null);
  const [titulo, setTitulo] = useState("Dados Agendamento");
  const [saving, setSaving] = useState(false);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loadedSchedule, setLoadedSchedule] = useState<ScheduleResponse | null>(
    null
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isNewEvent, setIsNewEvent] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const loadedScheduleRef = useRef<ScheduleResponse | null>(null);
  const selectedEventRef = useRef<string | null | undefined>(selectedEvent);

  useEffect(() => {
    if (selectedEvent === null || selectedEvent === undefined) {
      setTitulo("Adicionar Agendamento");
      setIsEditing(true);
      setIsNewEvent(true);
      return;
    }

    setTitulo("Dados Agendamento");
    setIsEditing(false);
    setIsNewEvent(false);
  }, [selectedEvent]);

  useEffect(() => {
    selectedEventRef.current = selectedEvent;
  }, [selectedEvent]);

  useEffect(() => {
    loadedScheduleRef.current = loadedSchedule;
  }, [loadedSchedule]);

  useEffect(() => {
    const sportCourtsFromRedux = SportCourtReduxWorker.getActiveSportCourts();
    if (sportCourtsFromRedux) {
      setSportCourts(sportCourtsFromRedux);
    }
  }, []);

  const loadContacts = useCallback(
    (filterQuery: typeof queryInitialState.filter) => {
      contactAPI.query(filterQuery).then((response) => {
        const resultAPI = response.data as ContactQueryResponse;
        setListaContatos(
          resultAPI.entities.map((contact: Contact) => ({
            id: contact.id,
            label: contact.name,
          }))
        );
      });
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    loadContacts(queryInitialState.filter);
  }, [loadContacts, open]);

  useEffect(() => {
    companyAPI.getIntegrations().then((response) => {
      setStripeEnabled(response.data.stripeEnabled ?? false);
    });
  }, []);

  const reloadSchedule = useCallback(async () => {
    const eventId = selectedEventRef.current;
    if (!eventId) return;
    const response = await scheduleAPI.getById(eventId);
    const scheduleFromAPI = response.data;
    setLoadedSchedule(scheduleFromAPI);
    loadedScheduleRef.current = scheduleFromAPI;
    setAppointment((prev) =>
      prev ? { ...prev, status: scheduleFromAPI.status } : prev
    );
  }, []);

  const handleContactChange = (
    _: React.SyntheticEvent,
    value: AutocompleteOption | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === "selectOption") {
      setSelectedContact(value);
      return;
    }

    if (reason === "clear") {
      setSelectedContact(null);
    }
  };

  const multiChange = (
    _: React.SyntheticEvent,
    value: AutocompleteValue<Availability, true, false, false>
  ) => {
    setSelectedAvailability(value as Availability[]);
  };

  function convertDate(date: Date | string): string {
    if (date instanceof Date) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${year}-${month}-${day}`;
    }

    if (typeof date === "string") {
      const dateParts = date.split("/");
      if (dateParts.length === 3) {
        const [day, month, year] = dateParts;
        return `${year}-${month}-${day}`;
      }
      return date;
    }

    return "";
  }

  const updateAvailability = useCallback(
    async (
      sportCourtId: string,
      whenDate: Date | string,
      scheduleToKeepSelection: ScheduleResponse | null = loadedScheduleRef.current,
      selectedEventId: string | null | undefined = selectedEventRef.current
    ) => {
      const formattedDate = convertDate(whenDate);
      const queryData = {
        from: formattedDate,
        to: formattedDate,
        sportCourtId,
      };

      const response = await sportCourtAPI.listTime(queryData);
      const scheduleFreeTime = response.data as ScheduleFreeTime;
      const availabilityList: Availability[] = Object.entries(
        scheduleFreeTime.schedules
      ).map(([timeFromTo, availabilityItem]) => ({
        sportCourtTaxId: availabilityItem.sportCourtTaxId,
        rented: availabilityItem.rented,
        value: availabilityItem.value,
        timeFromTo,
        rentalTime: availabilityItem.rentalTime,
        label: getAvailabilityLabel(timeFromTo, availabilityItem.value),
      }));

      setAvailability(availabilityList.filter((item) => !item.rented));

      if (
        selectedEventId !== null &&
        selectedEventId !== undefined &&
        scheduleToKeepSelection
      ) {
        const currentAvailability = availabilityList.find(
          (item) =>
            item.timeFromTo === scheduleToKeepSelection.timeFromTo &&
            item.value === scheduleToKeepSelection.rentalData.sportCourtValue
        );

        if (!currentAvailability) {
          setAvailabilityBlocked(true);
          setSelectedAvailability([]);
          return;
        }

        setAvailabilityBlocked(false);
        setSelectedAvailability([currentAvailability]);
        setScheduleForm((prev) => ({
          ...prev,
          availability: [currentAvailability],
        }));
      }
    },
    []
  );

  useEffect(() => {
    if (!open) return;

    let isActive = true;

    if (selectedEvent === null || selectedEvent === undefined) {
      const initialValues = getInitialValues();
      setScheduleForm(initialValues);
      setSelectedContact(initialValues.selectedContact);
      setSelectedAvailability([]);
      setAppointment(null);
      setLoadedSchedule(null);
      loadedScheduleRef.current = null;
      setAvailabilityBlocked(false);
      return;
    }

    const loadSchedule = async () => {
      const response = await scheduleAPI.getById(selectedEvent);
      if (!isActive) {
        return;
      }

      const scheduleFromAPI = response.data;
      setIsEditing(false);
      setTitulo("Dados Agendamento");
      setLoadedSchedule(scheduleFromAPI);
      loadedScheduleRef.current = scheduleFromAPI;

      const contact: AutocompleteOption = {
        id: scheduleFromAPI.contactId,
        label: scheduleFromAPI.contactName,
      };

      const appointmentData: Appointment = {
        ...scheduleFromAPI,
        id: scheduleFromAPI.id || "",
        schedulePlanId: scheduleFromAPI.schedulePlanId ?? null,
        contactId: scheduleFromAPI.contactId || "",
        contactName: scheduleFromAPI.contactName || "N/A",
        observation: scheduleFromAPI.observation || "",
        whenDate: scheduleFromAPI.whenDate || new Date().getTime(),
        responsibleId: String(scheduleFromAPI.responsibleId) || "",
        timeFromTo: scheduleFromAPI.timeFromTo || "N/A",
        rentalData: scheduleFromAPI.rentalData,
        type:
          scheduleFromAPI.type === "RENT" ||
          scheduleFromAPI.type === "OTHER_TYPE"
            ? scheduleFromAPI.type
            : "OTHER_TYPE",
        status: scheduleFromAPI.status,
      };

      setAppointment(appointmentData);
      setSelectedContact(contact);
      setScheduleForm({
        selectedContact: contact,
        sportCourtId: scheduleFromAPI.rentalData.sportCourtId,
        date: new Date(scheduleFromAPI.whenDate),
        availability: [],
        observation: scheduleFromAPI.observation || "",
        status: scheduleFromAPI.status,
         isRecurring: false,
         startDate: new Date(scheduleFromAPI.whenDate),
         endDate: new Date(scheduleFromAPI.whenDate),
         recurrenceFrequency: "WEEKLY",
         recurrenceDaysOfWeek: getDayOfWeekFromDate(
           new Date(scheduleFromAPI.whenDate)
         ),
      });

      await updateAvailability(
        scheduleFromAPI.rentalData.sportCourtId,
        moment(scheduleFromAPI.whenDate).format("YYYY-MM-DD"),
        scheduleFromAPI,
        selectedEvent
      );
    };

    void loadSchedule();

    return () => {
      isActive = false;
    };
  }, [getInitialValues, open, selectedEvent, updateAvailability]);

  const handleChangeSportCourtOrDate = async (
    sportCourtId: string,
    date: Date | null
  ) => {
    if (sportCourtId && date) {
      await updateAvailability(sportCourtId, date);
    }
  };

  const validationSchema = useMemo(() => scheduleModalValidationSchema, []);

  const handleOnSubmit = async (formValues: ScheduleModalFormValues) => {
    setSaving(true);

    const normalizedSelectedAvailability = normalizeAvailabilitySelection(
      selectedAvailability,
      formValues.isRecurring
    );

    if (
      selectedContact === null ||
      normalizedSelectedAvailability.length === 0
    ) {
      setSaving(false);
      return;
    }

    const responsibleId =
      typeof user?.userId === "string" && user.userId.trim().length > 0
        ? user.userId
        : null;

    if (!responsibleId) {
      toast.error(
        "Usuário responsável não identificado. Faça login novamente."
      );
      setSaving(false);
      return;
    }

    try {
      if (selectedEvent === null || selectedEvent === undefined) {
        if (formValues.isRecurring) {
          const payload = buildSchedulePlanPayload({
            formValues,
            selectedContact,
            selectedAvailability: normalizedSelectedAvailability,
            responsibleId,
          });

          const result = await schedulePlanAPI.create(payload);

          if (isSuccessStatus(result.status)) {
            startSchedulePlanProcessingMonitor({
              schedulePlanId: result.data.id,
              processingNotification: result.data.processingNotification,
            });
            handleClose(true);
            setIsEditing(false);
          } else {
            toast.error("Erro ao criar o plano recorrente!");
          }
          return;
        }

        const payload = buildScheduleCreatePayload({
          formValues,
          selectedContact,
          selectedAvailability: normalizedSelectedAvailability,
          responsibleId,
        });

        const result = await scheduleAPI.save(payload);

        if (isSuccessStatus(result.status)) {
          toast.success("Agendamento realizado com sucesso!");
          handleClose(true);
          setIsEditing(false);
        } else {
          toast.error("Erro ao realizar o agendamento!");
        }
        return;
      }

      const scheduleToSave = buildScheduleCreatePayload({
        formValues,
        selectedContact,
        selectedAvailability: normalizedSelectedAvailability,
        responsibleId,
      }).schedules[0];

      const scheduleToUpdate: Schedule = {
        id: selectedEvent,
        responsibleId: scheduleToSave.responsibleId,
        contactId: selectedContact.id,
        contactName: selectedContact.label,
        observation: scheduleToSave.observation,
        rentalData: scheduleToSave.rentalData,
        status: scheduleToSave.status,
        type: scheduleToSave.type,
        timeFromTo: scheduleToSave.timeFromTo,
        whenDate: scheduleToSave.whenDate,
      };

      const result = await scheduleAPI.update(selectedEvent, scheduleToUpdate);
      if (isSuccessStatus(result.status)) {
        toast.success("Agendamento atualizado com sucesso!");
        handleClose(true);
        setIsEditing(false);
      } else {
        toast.error("Erro ao atualizar o agendamento!");
      }
    } catch (error) {
      const defaultMessage =
        selectedEvent === null || selectedEvent === undefined
          ? formValues.isRecurring
            ? "Erro ao criar o plano recorrente!"
            : "Erro ao realizar o agendamento!"
          : "Erro ao atualizar o agendamento!";
      toast.error(extractApiErrorMessage(error, defaultMessage));
    } finally {
      setSaving(false);
    }
  };

  const handleModalClose = () => {
    setSelectedContact(null);
    setScheduleForm(getInitialValues());
    setAvailability([]);
    setAvailabilityBlocked(false);
    setSelectedAvailability([]);
    setSaving(false);
    setIsEditing(true);
    setTitulo("Adicionar Agendamento");
    setAppointment(null);
    setLoadedSchedule(null);
    loadedScheduleRef.current = null;
    handleClose(false);
  };

  const handleRemove = async (): Promise<void> => {
    if (!selectedEvent) return;

    try {
      await scheduleAPI.remove(selectedEvent);
      toast.success("Agendamento deletado com sucesso.");
      handleClose(true);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, "Erro ao remover agendamento"));
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setTitulo("Editar Agendamento");
  };

  const handleOpenSchedulePlan = () => {
    if (!appointment?.schedulePlanId) {
      return;
    }

    navigate(buildSchedulePlanDetailPath(appointment.schedulePlanId));
  };

  return (
    <Modal
      aria-labelledby="schedule-modal-title"
      aria-describedby="schedule-modal-description"
      open={open}
      onClose={handleModalClose}
      slots={{ backdrop: StyledBackdrop }}
    >
      <ModalContent>
        <HeaderSection>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography id="schedule-modal-title" variant="h6" fontWeight={700}>
                {titulo}
              </Typography>
              {appointment?.schedulePlanId ? (
                <Chip
                  label="Plano"
                  size="small"
                  clickable
                  onClick={handleOpenSchedulePlan}
                  sx={{
                    height: 22,
                    backgroundColor: "#FACC15",
                    color: "#111111",
                    fontWeight: 700,
                    "&:hover": {
                      backgroundColor: "#EAB308",
                    },
                    "& .MuiChip-label": {
                      px: 1.25,
                    },
                  }}
                />
              ) : null}
            </Stack>
            <Typography
              id="schedule-modal-description"
              variant="body2"
              color="text.secondary"
              mt={0.5}
            >
              {selectedEvent
                ? "Visualize ou edite os dados do agendamento"
                : "Preencha os dados para criar um novo agendamento"}
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            {selectedEvent ? (
              <ScheduleModalMenu handleRemove={handleRemove} />
            ) : null}
            <IconButton onClick={handleModalClose} aria-label="Fechar modal">
              <CloseIcon />
            </IconButton>
          </Stack>
        </HeaderSection>

        {selectedEvent && !isEditing ? (
          <AppointmentDetails
            appointment={appointment}
            onEdit={handleEdit}
            stripeEnabled={stripeEnabled}
            paymentCharge={loadedSchedule?.paymentCharge ?? null}
            onChargeCreated={reloadSchedule}
          />
        ) : null}

        {(selectedEvent || isNewEvent) && isEditing ? (
          <Formik
            initialValues={scheduleForm}
            onSubmit={handleOnSubmit}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {({
              values,
              setFieldValue,
              errors,
              handleChange,
              handleSubmit,
              touched,
            }) => (
              <Grid
                container
                spacing={2.5}
                component="form"
                onSubmit={handleSubmit}
              >
                <Grid item sm={6} xs={12}>
                  <AutocompleteSelect
                    id="contact"
                    label="Cliente"
                    stateValue={selectedContact}
                    options={listaContatos}
                    placeholder="Pesquise pelo nome do cliente"
                    onHandleChange={(
                      event: React.SyntheticEvent,
                      value: AutocompleteOption | null,
                      reason: AutocompleteChangeReason,
                      details?: AutocompleteChangeDetails<AutocompleteOption>
                    ) => {
                      handleContactChange(event, value, reason);
                      setFieldValue("selectedContact", value);
                    }}
                    error={Boolean(
                      touched.selectedContact && errors.selectedContact
                    )}
                    helperText={
                      touched.selectedContact ? errors.selectedContact : ""
                    }
                  />
                </Grid>

                <Grid item sm={6} xs={12}>
                  <LightSelect
                    labelId="sportCourtIdLabel"
                    id="sportCourtId"
                    name="sportCourtId"
                    label="Quadra"
                    value={values.sportCourtId}
                    onChange={(event) => {
                      handleChange(event);
                      void handleChangeSportCourtOrDate(
                        (event.target as HTMLInputElement).value,
                        values.date || null
                      );
                    }}
                    error={Boolean(touched.sportCourtId && errors.sportCourtId)}
                    helperText={touched.sportCourtId ? errors.sportCourtId : ""}
                  >
                    {sportCourts.map((sportCourt: SportCourt) => (
                      <MenuItem key={sportCourt.id} value={sportCourt.id}>
                        {sportCourt.name}
                      </MenuItem>
                    ))}
                  </LightSelect>
                </Grid>

                <Grid item sm={6} xs={12}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={ptBR}
                  >
                    <DatePicker
                      label={values.isRecurring ? "Data inicial" : "Data"}
                      value={values.date ?? null}
                      format="dd/MM/yyyy"
                      onChange={(newDate: Date | null) => {
                        setFieldValue("date", newDate);
                        if (values.isRecurring) {
                          setFieldValue("startDate", newDate);
                          if (
                            newDate &&
                            shouldRequireDaysOfWeek(values.recurrenceFrequency)
                          ) {
                            setFieldValue(
                              "recurrenceDaysOfWeek",
                              getDayOfWeekFromDate(newDate)
                            );
                          }
                        }
                        void handleChangeSportCourtOrDate(
                          values.sportCourtId,
                          newDate
                        );
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(touched.date && errors.date),
                          helperText: touched.date ? errors.date : "",
                          sx: (theme) => ({
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "14px",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? alpha(
                                      theme.palette.background.default,
                                      0.28
                                    )
                                  : theme.palette.background.paper,
                              transition:
                                "background-color 0.2s ease, border-color 0.2s ease",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderWidth: 2,
                              borderColor: "divider",
                            },
                            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                              {
                                borderColor: "primary.main",
                              },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                              {
                                borderColor: "primary.main",
                                borderWidth: 2,
                              },
                            "& .MuiInputBase-input": {
                              color: "text.primary",
                              fontWeight: 500,
                            },
                            "& .MuiSvgIcon-root": {
                              color: "text.secondary",
                            },
                          }),
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                {!selectedEvent ? (
                  <Grid item xs={12}>
                    <Stack
                      spacing={1.2}
                      sx={{
                        p: 1.75,
                        borderRadius: "16px",
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.background.default, 0.24)
                            : alpha(theme.palette.primary.light, 0.05),
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.isRecurring}
                            onChange={(_, checked) => {
                              const normalized = normalizeAvailabilitySelection(
                                selectedAvailability,
                                checked
                              );
                              setFieldValue("isRecurring", checked);
                              setSelectedAvailability(normalized);
                              setFieldValue("availability", normalized);

                              if (checked) {
                                const referenceDate =
                                  values.date ?? values.startDate ?? new Date();
                                setFieldValue("startDate", referenceDate);
                                setFieldValue(
                                  "recurrenceDaysOfWeek",
                                  shouldRequireDaysOfWeek(
                                    values.recurrenceFrequency
                                  )
                                    ? getDayOfWeekFromDate(referenceDate)
                                    : []
                                );
                              }
                            }}
                          />
                        }
                        label="Criar como recorrencia"
                      />

                      <Typography variant="body2" color="text.secondary">
                        Com a recorrencia ativada, o modal cria um plano
                        assicrono no backend e permite selecionar apenas um
                        horario base para a serie.
                      </Typography>

                      {values.isRecurring ? (
                        <Alert severity="info" variant="outlined">
                          O fluxo recorrente usa apenas um horario por plano.
                        </Alert>
                      ) : null}
                    </Stack>
                  </Grid>
                ) : null}

                {!values.isRecurring ? (
                  <Grid item sm={6} xs={12}>
                    <LightSelect
                      labelId="statusLabel"
                      id="status"
                      name="status"
                      label="Status"
                      value={values.status}
                      onChange={handleChange}
                      error={Boolean(touched.status && errors.status)}
                      helperText={touched.status ? errors.status : ""}
                    >
                      {SCHEDULE_STATUS.map((scheduleStatus) => (
                        <MenuItem
                          key={scheduleStatus.value}
                          value={scheduleStatus.value}
                        >
                          {scheduleStatus.name}
                        </MenuItem>
                      ))}
                    </LightSelect>
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <MultiScheduleSelect
                    disabled={Boolean(
                      values.sportCourtId === "" || availabilityBlocked
                    )}
                    id="availability"
                    label="Horários"
                    initialValues={selectedAvailability}
                    availability={availability}
                    onChange={(
                      event: React.SyntheticEvent,
                      value: AutocompleteValue<
                        Availability,
                        true,
                        false,
                        false
                      >,
                      reason: AutocompleteChangeReason,
                      details?: AutocompleteChangeDetails<Availability>
                    ) => {
                      const normalized = normalizeAvailabilitySelection(
                        value as Availability[],
                        values.isRecurring
                      );
                      multiChange(event, normalized);
                      setFieldValue("availability", normalized);
                    }}
                    error={Boolean(touched.availability && errors.availability)}
                    helperText={[
                      values.isRecurring
                        ? "Selecione apenas um horario para o plano recorrente."
                        : "",
                      touched.availability &&
                      typeof errors.availability === "string"
                        ? errors.availability
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  />
                </Grid>

                {values.isRecurring ? (
                  <>
                    <Grid item xs={12}>
                      <Divider flexItem />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Configuracao da recorrencia
                      </Typography>
                    </Grid>

                    <Grid item sm={6} xs={12}>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={ptBR}
                      >
                        <DatePicker
                          label="Data final"
                          value={values.endDate ?? null}
                          format="dd/MM/yyyy"
                          onChange={(newDate: Date | null) => {
                            setFieldValue("endDate", newDate);
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: Boolean(touched.endDate && errors.endDate),
                              helperText: touched.endDate ? errors.endDate : "",
                              sx: (theme) => ({
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "14px",
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? alpha(
                                          theme.palette.background.default,
                                          0.28
                                        )
                                      : theme.palette.background.paper,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderWidth: 2,
                                  borderColor: "divider",
                                },
                              }),
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item sm={3} xs={12}>
                      <LightSelect
                        labelId="recurrenceFrequencyLabel"
                        id="recurrenceFrequency"
                        name="recurrenceFrequency"
                        label="Frequencia"
                        value={values.recurrenceFrequency}
                        onChange={(event) => {
                          handleChange(event);
                          const nextFrequency = (
                            event.target as HTMLInputElement
                          ).value as typeof values.recurrenceFrequency;
                          if (shouldRequireDaysOfWeek(nextFrequency)) {
                            setFieldValue(
                              "recurrenceDaysOfWeek",
                              values.recurrenceDaysOfWeek.length
                                ? values.recurrenceDaysOfWeek
                                : getDayOfWeekFromDate(
                                    values.date ?? values.startDate
                                  )
                            );
                          } else {
                            setFieldValue("recurrenceDaysOfWeek", []);
                          }
                        }}
                        error={Boolean(
                          touched.recurrenceFrequency &&
                            errors.recurrenceFrequency
                        )}
                        helperText={
                          touched.recurrenceFrequency
                            ? errors.recurrenceFrequency
                            : "Escolha entre recorrencia semanal ou mensal."
                        }
                      >
                        {RECURRENCE_FREQUENCY_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </LightSelect>
                    </Grid>

                    {shouldRequireDaysOfWeek(values.recurrenceFrequency) ? (
                      <Grid item xs={12}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            Dias da semana
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                          >
                            {DAY_OF_WEEK_OPTIONS.map((dayOption) => {
                              const selected =
                                values.recurrenceDaysOfWeek.includes(
                                  dayOption.value
                                );

                              return (
                                <Chip
                                  key={dayOption.value}
                                  label={dayOption.label}
                                  color={selected ? "primary" : "default"}
                                  variant={selected ? "filled" : "outlined"}
                                  onClick={() => {
                                    const nextDays = selected
                                      ? values.recurrenceDaysOfWeek.filter(
                                          (day) => day !== dayOption.value
                                        )
                                      : [
                                          ...values.recurrenceDaysOfWeek,
                                          dayOption.value,
                                        ];

                                    setFieldValue(
                                      "recurrenceDaysOfWeek",
                                      nextDays
                                    );
                                  }}
                                />
                              );
                            })}
                          </Stack>
                          {touched.recurrenceDaysOfWeek &&
                          typeof errors.recurrenceDaysOfWeek === "string" ? (
                            <Typography variant="caption" color="error">
                              {errors.recurrenceDaysOfWeek}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Grid>
                    ) : null}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <LightTextArea
                      name="observation"
                      label="Observação"
                      maxRows={4}
                      maxLength={200}
                      value={values.observation}
                      onChange={handleChange}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="flex-end">
                    <Button type="submit" disabled={saving} variant="contained">
                      {saving
                        ? "Salvando..."
                        : values.isRecurring
                          ? "Criar plano recorrente"
                          : t("Salvar")}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Formik>
        ) : null}
      </ModalContent>
    </Modal>
  );
};

const Modal = styled(BaseModal)`
  position: fixed;
  z-index: 1300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const StyledBackdrop = styled("div")`
  z-index: -1;
  position: fixed;
  inset: 0;
  background-color: rgb(0 0 0 / 0.45);
  backdrop-filter: blur(3px);
  -webkit-tap-highlight-color: transparent;
`;

const ModalContent = styled("div")(
  ({ theme }) => css`
    width: min(760px, 100%);
    max-height: calc(100vh - 48px);
    overflow: auto;
    font-weight: 500;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 14px;
    background-color: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
    border-radius: 20px;
    border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
    box-shadow: 0 24px 60px
      ${theme.palette.mode === "dark"
        ? "rgb(0 0 0 / 0.55)"
        : "rgb(11 31 53 / 0.2)"};
    padding: 22px;
    color: ${theme.palette.mode === "dark" ? grey[50] : grey[900]};
  `
);

const grey = {
  50: "#F3F6F9",
  200: "#DAE2ED",
  700: "#434D5B",
  900: "#1C2025",
};

const HeaderSection = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  paddingBottom: 6,
  borderBottom: `1px solid ${theme.palette.secondary[200]}`,
}));

export default ScheduleModal;
